import React, { useCallback } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { ChevronDownOutline } from '@lyearn/icons';
import { cn, css } from '@lyearn/style';
import { Box, LinkButton, Stack, Typography } from '@lyearn/ui';
import { RecommendedActionStatusEnum } from '@/types/schema';

import { InsightsRecommendationItem, UpdateRecommendationStatusProps } from '../types';
import InsightRowAction from './InsightRowAction';
import InsightRowIcon from './InsightRowIcon';
import InsightRowReasons from './InsightRowReasons';

const rotateButtonIcon = (degrees: number) => css`
  svg {
    transition: transform 0.2s ease;
    transform: rotate(${degrees}deg);
  }
`;

const balanceText = css`
  text-wrap: balance;
`;

interface Props {
  recommendation: InsightsRecommendationItem;
  updateRecommendationStatus: (props: UpdateRecommendationStatusProps) => void;
}

function InsightRowMini(props: Props) {
  const { recommendation, updateRecommendationStatus } = props;

  const [showReasons, { toggle: toggleReasons }] = useToggle();
  const hasReasons = recommendation.card?.reasons && recommendation.card?.reasons.length > 0;

  const updateRecommendationStatusMutation = useCallback(() => {
    updateRecommendationStatus({
      recommendationId: recommendation.id,
      status: RecommendedActionStatusEnum.Seen,
    });
  }, [recommendation.id, updateRecommendationStatus]);

  const onRowClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (e) => {
      // Prevent toggling reasons when clicking on action button
      if ((e.target as HTMLElement).closest('button')) {
        return;
      }
      // Optimistically update the status
      updateRecommendationStatusMutation();

      if (hasReasons) {
        toggleReasons();
      }
    },
    [updateRecommendationStatusMutation, hasReasons, toggleReasons],
  );

  const onReasonClick = useCallback(() => {
    updateRecommendationStatusMutation();
    toggleReasons();
  }, [updateRecommendationStatusMutation, toggleReasons]);

  return (
    <Stack className="py-20" direction="col" onClick={onRowClick}>
      <Stack alignItems="start" direction="row">
        <InsightRowIcon recommendation={recommendation} />
        <Typography
          className={cn('ms-16 pe-50 flex-1', balanceText)}
          color="text-text-primary"
          variant="label-02">
          {recommendation.card?.title}
        </Typography>
        <Box
          className={cn(
            'w-8 h-8 rounded-full bg-icon-error shrink-0',
            recommendation.status === RecommendedActionStatusEnum.New ? '' : 'opacity-0',
          )}
        />
      </Stack>

      <Stack alignItems="center" className="ms-64 mt-4" direction="row" justifyContent="between">
        {hasReasons ? (
          <LinkButton className="cursor-pointer flex items-center gap-4" onClick={onReasonClick}>
            <Typography color="text-text-secondary" variant="label-03">
              See Why
            </Typography>
            <ChevronDownOutline
              className={rotateButtonIcon(showReasons ? 180 : 0)}
              fillColorToken="icon-secondary"
            />
          </LinkButton>
        ) : (
          <Box className="h-2 w-2 shrink-0" />
        )}
        <InsightRowAction recommendation={recommendation} />
      </Stack>

      {hasReasons ? (
        <InsightRowReasons
          className="ms-64 mt-8"
          open={showReasons}
          recommendation={recommendation}
          showFLowLine={false}
        />
      ) : null}
    </Stack>
  );
}

export default React.memo(InsightRowMini);
